import * as React from 'react';
import { cn } from './lib/utils';

/**
 * Small colored dot for real-time status indicators.
 *
 * status="live"        — green, pulsing animation (data is live)
 * status="stale"       — yellow, static (data is outdated / delayed)
 * status="error"       — red, static (feed error / connection failed)
 * status="offline"     — gray, static (service offline / disconnected)
 * status="processing"  — aqua, spinning (action in progress)
 *
 * size="sm"  → 6px
 * size="md"  → 8px (default)
 * size="lg"  → 10px
 */

const STATUS = {
  live:       { color: 'bg-green-nn',    pulse: true,  spin: false },
  stale:      { color: 'bg-yellow-nn',   pulse: false, spin: false },
  error:      { color: 'bg-red-critical',pulse: false, spin: false },
  offline:    { color: 'bg-text-muted',  pulse: false, spin: false },
  processing: { color: 'bg-aqua',        pulse: false, spin: true  },
};

const SIZE_CLASS = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
  lg: 'h-2.5 w-2.5',
};

function StatusDot({ status = 'offline', size = 'md', className, label, ...props }) {
  const s = STATUS[status] ?? STATUS.offline;
  const sizeClass = SIZE_CLASS[size] ?? SIZE_CLASS.md;

  return (
    <span
      role="img"
      aria-label={label ?? status}
      className={cn('relative inline-flex shrink-0', sizeClass, className)}
      {...props}
    >
      {s.pulse && (
        <span
          className={cn(
            'absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping',
            s.color
          )}
        />
      )}
      <span
        className={cn(
          'relative inline-flex rounded-full',
          sizeClass,
          s.color,
          s.spin && 'animate-spin'
        )}
      />
    </span>
  );
}

export { StatusDot };
